import type {
  MouseEvent,
  MouseEventHandler,
  PointerEvent,
  ReactNode,
} from 'react'
import {
  Cell,
  ColumnDef,
  SortingState,
  Table as TableInstance,
  VisibilityState,
} from '@tanstack/react-table'

import type { TableColumnFilterValue } from '@scrambl/shared'

export type ColumnFilterValue = TableColumnFilterValue
export type ColumnDropPosition = 'before' | 'after'
export type RowDropPosition = 'above' | 'below'

/**
 * Utility/system column ids that are never part of the user-reorderable column
 * set. These are appended by the table (spacer/customize) or supplied as
 * non-reorderable extra columns (action menus, selection, etc.) and live
 * outside of `visibleColumns`. They must be excluded from both the draggable
 * headers and the drop-target resolution — otherwise dragging toward the right
 * edge resolves the drop onto one of these columns, which isn't in
 * `visibleColumns`, and the reorder silently no-ops.
 */
export const NON_REORDERABLE_COLUMN_IDS = new Set([
  'view',
  '_customize',
  'spacer',
  'actions',
  'action',
  'rowActions',
  'select',
  'drag',
])

export interface ColumnDropIndicator {
  targetColumnId: string
  position: ColumnDropPosition
}

/**
 * Callbacks that let a row drag interact with drop targets rendered outside
 * this table (e.g. dragging tasks onto a sprint card that hosts a different
 * table instance). All callbacks fire from the table's row-drag lifecycle.
 */
export interface ExternalRowDropSession {
  onStart: (rowId: string) => void
  /**
   * Streamed on every pointer move during the drag. Return true while the
   * pointer is over an external drop target — the table then suppresses its
   * internal row drop indicator so the two targets never compete.
   */
  onMove: (pointer: { x: number; y: number }) => boolean
  /**
   * Fired on drop, before the internal reorder. Return true when the drop was
   * handled externally — the table then skips its internal row reorder.
   */
  onDrop: (rowId: string) => boolean
  onCancel: () => void
}

// Enhanced column type for our DataTable
export interface DataTableColumn<T> {
  id: string
  header: ReactNode
  label?: string // Human-readable label (fallback: string header or id)
  description?: string // Shown as an info tooltip in the column header
  cell: (item: T) => ReactNode
  accessorFn?: (row: T) => unknown
  enableSorting?: boolean
  // Client-side sort: push null/empty values to the end regardless of direction
  // (otherwise TanStack coerces null→0 and they sort between negatives/positives).
  sortNullsLast?: boolean
  manualSorting?: boolean
  enableFiltering?: boolean
  enableResizing?: boolean
  className?: string
  hidden?: boolean
  width?: string
  minWidth?: string
  maxWidth?: string
  fieldType?: string // Field type for filtering (e.g., 'STRING', 'NUMBER', 'DATE')
  customFieldDefinitionId?: string // ID of the custom field definition (for edit functionality)
  dropdownOptions?: Array<{ value: string; label: string }> // Options for DROPDOWN field type filters
  userOptions?: Array<{ id: string; name: string; email?: string }> // Options for USER field type filters
  hideColumnMenu?: boolean // Hide sort/filter/options menu for utility columns
  disableCellSelection?: boolean
  // Column is visible to client portal users; shows a muted indicator on header hover
  clientPortalVisibility?: 'view' | 'edit'
  // Cell-level click handling
  onCellClick?: (item: T, event: MouseEvent) => void
  onCellDoubleClick?: (item: T, event: MouseEvent) => void
}

// Column item type for table customizer
export interface ColumnItem {
  id: string
  label: string
  description?: string
  isVisible: boolean
  isFixed?: boolean
  isSystem?: boolean
  isCustomField?: boolean
  order?: number
}

// Interface for column meta data
export interface ColumnMeta {
  className?: string
  width?: string
  minWidth?: string
  maxWidth?: string
  enableResizing?: boolean
  isFixed?: boolean
  label?: string // Human-readable label for column menus and tooltips
  description?: string // Shown as an info tooltip in the column header
  fieldType?: string // Field type for filtering (e.g., 'STRING', 'NUMBER', 'DATE')
  customFieldDefinitionId?: string // ID of the custom field definition (for edit functionality)
  dropdownOptions?: Array<{ value: string; label: string }> // Options for DROPDOWN field type filters
  userOptions?: Array<{ id: string; name: string; email?: string }> // Options for USER field type filters
  hideColumnMenu?: boolean // Hide sort/filter/options menu for utility columns
  clientPortalVisibility?: 'view' | 'edit' // Column is visible to client portal users
  // Spacer/customize column metadata
  enableCustomizeColumn?: boolean
  fieldTypeOptions?: Array<{
    value: string
    label: string
    description: string
  }>
  hiddenColumns?: Array<{ id: string; label: string; description?: string }>
  onEnableColumn?: (columnId: string) => void
  onFieldTypeSelect?: (fieldType: string) => void
  customizePopoverKey?: string
  // Table customizer props
  allColumns?: ColumnItem[]
  onColumnVisibilityChange?: (
    columnId: string,
    isVisible: boolean,
  ) => Promise<void>
  onColumnsReorder?: (reorderedColumns: ColumnItem[]) => Promise<void>
  showCustomizeButton?: boolean
  canModifyColumns?: boolean
}

// Table reference interface
export interface DataTableRef<T> {
  table: TableInstance<T>
  expandAllGroups: () => void
  collapseAllGroups: () => void
  resetColumnWidths: () => void
}

// Main DataTable props interface
export interface DataTableProps<T> {
  data: T[]
  columns: DataTableColumn<T>[]
  onRowClick?: (item: T) => void
  isLoading?: boolean
  loadingRows?: Set<string>
  emptyState?: ReactNode
  className?: string
  tableContainerClassName?: string
  rowClassName?: string | ((row: T) => string)
  enableSorting?: boolean
  manualSorting?: boolean
  enableFiltering?: boolean
  enableRowSelection?: boolean
  enableColumnVisibility?: boolean
  enableCellSelection?: boolean
  enableColumnResizing?: boolean
  enableColumnReordering?: boolean
  showControls?: boolean
  debugMode?: boolean
  onSortingChange?: (sorting: SortingState) => void
  onColumnVisibilityChange?: (visibility: VisibilityState) => void
  onColumnResize?: (columnId: string, width: number) => void
  onColumnResizeEnd?: (columnId: string, width: number) => void
  /** Keep this table synchronized with externally managed column widths. */
  syncColumnWidths?: boolean
  onColumnReorder?: (
    activeColumnId: string,
    overColumnId: string,
    position: ColumnDropPosition,
  ) => Promise<void>
  state?: {
    sorting?: SortingState
    columnVisibility?: VisibilityState
    columnWidths?: Record<string, number>
  }
  getRowId?: (row: T) => string
  hideHeader?: boolean
  /**
   * Virtualize table rows (only mount rows near the viewport). Requires a
   * `.sticky-header-wrapper` scroll ancestor and silently falls back to full
   * rendering otherwise. Variable-height custom rows are measured after mount.
   */
  enableVirtualization?: boolean
  /**
   * Override how the table finds its scroll viewport. Page-scroller pages
   * (PageContainer `layout="flow"`) hand back the page column, because the
   * table's own `.sticky-header-wrapper` no longer scrolls there. Returning
   * null falls back to the nearest `.sticky-header-wrapper` ancestor.
   */
  resolveScrollElement?: (start: HTMLElement | null) => HTMLElement | null
  // Group expand/collapse functionality
  enableGroupExpansion?: boolean
  defaultExpandedGroups?: Set<string>
  onGroupExpansionChange?: (expandedGroups: Set<string>) => void
  getGroupId?: (row: T) => string | null
  // Drag and drop functionality
  enableRowReordering?: boolean
  isDraggableRow?: (row: T) => boolean
  onRowReorder?: (
    activeId: string,
    overId: string,
    position: RowDropPosition,
  ) => Promise<void>
  onDragOver?: (
    activeId: string,
    overId: string,
    position: RowDropPosition,
  ) => void
  onDragEnd?: () => void
  getDragId?: (row: T) => string
  // Render group header rows as drop targets (data-drag-id) even when the
  // headers themselves aren't draggable, so onRowReorder/onDragOver can
  // receive a group row as overId (e.g. "drop on bucket header inserts at the
  // top of the bucket"). Off by default: tables whose reorder callbacks don't
  // handle group rows would otherwise turn near-header drops into no-ops.
  droppableGroupHeaders?: boolean
  // Bridge for drop targets that live OUTSIDE this table (each DataTable has
  // its own DndContext, so cross-table targets can't be dnd-kit droppables).
  // The table drives the callbacks from its row-drag lifecycle; the consumer
  // resolves external targets from the streamed pointer position.
  externalDropSession?: ExternalRowDropSession
  sortableRowIds?: string[] // Override for sortable context IDs
  dropIndicator?: {
    targetId: string
    position: 'above' | 'below' | 'inside'
  } | null
  showDragHandle?: boolean
  // Custom drag overlay renderer - receives the dragged item and returns a ReactNode
  renderDragOverlay?: (item: T) => ReactNode
  // Customize column functionality
  enableCustomizeColumn?: boolean
  onOpenColumnSettings?: () => void
  onAddCustomField?: () => void
  hiddenColumns?: Array<{ id: string; label: string; description?: string }>
  onEnableColumn?: (columnId: string) => void
  // Column management functionality
  onHideColumn?: (columnId: string) => void
  onToggleSort?: (columnId: string) => void
  // Custom field editing functionality
  onEditField?: (customFieldDefinitionId: string) => void
  // Inline column rename (primary/system columns without a field definition)
  onRenameColumn?: (columnId: string, newLabel: string) => void
  // Column filtering functionality
  columnFilters?: Record<string, ColumnFilterValue>
  onColumnFilterChange?: (
    columnId: string,
    value: ColumnFilterValue | null,
  ) => void
  // Field type selection functionality
  fieldTypeOptions?: Array<{
    value: string
    label: string
    description: string
  }>
  onFieldTypeSelect?: (fieldType: string) => void
  customizePopoverKey?: string
  // Append row - renders as the last row in the table body
  appendRow?: ReactNode
  // Hide the column header menu (sort/filter/hide options)
  hideColumnMenu?: boolean
  // Table customizer props
  allColumns?: ColumnItem[]
  onTableColumnVisibilityChange?: (
    columnId: string,
    isVisible: boolean,
  ) => Promise<void>
  onTableColumnsReorder?: (reorderedColumns: ColumnItem[]) => Promise<void>
  showCustomizeButton?: boolean
  canModifyColumns?: boolean
  showPendingUpdateIndicators?: boolean
  // Custom row renderer — called for each row before default rendering.
  // Return a ReactNode to replace default rendering, or null/undefined to use default.
  renderCustomRow?: (
    item: T,
    columnCount: number,
  ) => ReactNode | null | undefined
  // Field-aware paste handler. Fired when the user presses Cmd/Ctrl+V on a
  // selected (non-editing) cell. Receives the row id, column id, and the
  // clipboard text. The consumer is responsible for coercing the text into
  // the correct shape and dispatching the field update.
  onCellPaste?: (
    rowId: string,
    columnId: string,
    text: string,
  ) => Promise<void> | void
  onCellsPaste?: (
    updates: Array<{ rowId: string; columnId: string; text: string }>,
  ) => Promise<void> | void
  // Field-aware clear handler. Fired when the user presses Delete/Backspace on
  // a selected (non-editing) cell. The consumer is responsible for deciding
  // whether the field can be cleared and dispatching the field update.
  onCellClear?: (rowId: string, columnId: string) => Promise<void> | void
  onCellsClear?: (
    cells: Array<{ rowId: string; columnId: string }>,
  ) => Promise<void> | void
}

// Table cell props
export interface TableCellProps {
  cell: Cell<unknown, unknown>
  isRowLoading?: boolean
  isDraggable?: boolean
  isDraggingTable?: boolean
  isScrolling?: boolean
  isActivelyResizing?: boolean
  columnWidth?: number
  rowId?: string
  isSelected?: boolean
  isActiveSelection?: boolean
  isFillPreview?: boolean
  showFillHandle?: boolean
  enableCellSelection?: boolean
  // Cell-level click handling
  item?: unknown
  disableCellSelection?: boolean
  onCellClick?: (item: unknown, event: MouseEvent) => void
  onCellDoubleClick?: (item: unknown, event: MouseEvent) => void
  onCellSelect?: (
    rowId: string,
    columnId: string,
    cell: HTMLTableCellElement,
    options?: { extend?: boolean; startDrag?: boolean },
  ) => void
  onFillHandlePointerDown?: (event: PointerEvent<HTMLButtonElement>) => void
}

// Table row props
export interface TableRowProps {
  children: ReactNode
  isDraggable: boolean
  dragId: string
  rowId?: string
  className?: string
  onClick?: MouseEventHandler<HTMLTableRowElement>
  dropIndicator?: {
    targetId: string
    position: 'above' | 'below' | 'inside'
  } | null
  showDragHandle?: boolean
  showPendingUpdateIndicator?: boolean
  /** Lets the table virtualizer measure variable-height rows after mount. */
  measureElement?: (element: HTMLTableRowElement | null) => void
}

// Column resizing hook return type
export interface UseColumnResizingReturn {
  columnWidths: Record<string, number>
  isActivelyResizing: boolean
  handleColumnResize: (columnId: string, width: number) => void
  handleColumnResizeEnd: (columnId: string, width: number) => void
  resetColumnWidths: () => void
}

// Table columns hook return type - properly typed for TanStack Table
export interface UseTableColumnsReturn<T = unknown> {
  memoizedColumns: ColumnDef<T>[]
  columnsWithCustomize: ColumnDef<T>[]
  currentSortColumn: string | null
  isDescending: boolean
}
